import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import type { FeatureId } from '@sequesto-os/shared';
import { useAccess } from '../../hooks/useAccess';
import { PageHeader, PageShell } from '../../components/ui/PageHeader';
import { TabBar, type TabItem } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/States';
import { AccountsView } from './AccountsView';
import { ChannelsView } from './ChannelsView';
import { MatchReviewView } from './MatchReviewView';
import { HealthView } from './HealthView';
import { ModelView } from './ModelView';
import { SourcingView } from './SourcingView';
import { PlaysView } from './PlaysView';
import { FunnelView } from './FunnelView';
import { PointsView } from './PointsView';

/**
 * GTM — everything before the demo: who to chase next, the signal campaigns
 * that reach them, and which channels create pipeline.
 *
 * Attio keeps the pipeline (stages, owners, loss reasons) and receives outcomes;
 * this section keeps raw signals, scoring, attribution and data health.
 */
type Section = 'accounts' | 'funnel' | 'points' | 'sourcing' | 'plays' | 'channels' | 'model' | 'matches' | 'health';

/** What the team works from. */
const TEAM_SECTIONS: readonly TabItem<Section>[] = [
  { id: 'accounts', label: 'Accounts' },
  { id: 'funnel', label: 'Funnel' },
  { id: 'points', label: 'Points' },
  { id: 'sourcing', label: 'Sourcing' },
  { id: 'plays', label: 'Plays' },
  { id: 'channels', label: 'Channels' },
];

/** The engine room — how the signals are built and kept clean. Admins only. */
const ADMIN_SECTIONS: readonly TabItem<Section>[] = [
  { id: 'model', label: 'Signal model' },
  { id: 'matches', label: 'Match review' },
  { id: 'health', label: 'Data health' },
];

const SECTION_FEATURE: Record<Section, FeatureId> = {
  accounts: 'gtm.accounts',
  // Same audience as Accounts: the stage is the second axis of the same list.
  funnel: 'gtm.accounts',
  // Readable by everyone who sees Accounts; only admins can change a value.
  points: 'gtm.accounts',
  sourcing: 'gtm.sourcing',
  plays: 'gtm.plays',
  channels: 'gtm.channels',
  model: 'gtm.model',
  matches: 'gtm.matches',
  health: 'gtm.health',
};

/** Old URLs kept working. The re-tender diary is now a view inside Sourcing. */
const LEGACY_REDIRECTS: Record<string, string> = {
  diary: '/gtm/sourcing?view=diary',
};

export function GTM() {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const { canAccess, isAdmin } = useAccess();

  const visibleSections = [...TEAM_SECTIONS, ...(isAdmin ? ADMIN_SECTIONS : [])].filter((s) =>
    canAccess(SECTION_FEATURE[s.id]),
  );
  const firstSection = visibleSections[0]?.id;

  if (section && LEGACY_REDIRECTS[section]) {
    return <Navigate to={LEGACY_REDIRECTS[section]} replace />;
  }
  const active = visibleSections.find((s) => s.id === section)?.id;
  if (!active && (!section || !firstSection)) {
    return firstSection ? <Navigate to={`/gtm/${firstSection}`} replace /> : <Navigate to="/" replace />;
  }
  if (!active) {
    // A mistyped or old /gtm/<tab> used to land silently on Accounts: say so, with the tabs one click away.
    const known = [...TEAM_SECTIONS, ...ADMIN_SECTIONS].find((s) => s.id === section);
    return (
      <PageShell>
        <PageHeader eyebrow="Departments" title="GTM" subtitle="Who to chase next and which channels create pipeline." />
        <EmptyState
          title={known ? `${known.label} is not open to you` : 'No such GTM tab'}
          description={
            known ? (
              <>
                <span className="font-mono">/gtm/{section}</span> needs access you don’t have.
              </>
            ) : (
              <>
                Nothing lives at <span className="font-mono">/gtm/{section}</span>. The link may be old or mistyped.
              </>
            )
          }
          action={
            <span className="inline-flex flex-wrap justify-center gap-3">
              {visibleSections.map((s) => (
                <Link key={s.id} to={`/gtm/${s.id}`} className="text-body-sm font-medium text-accent hover:underline">
                  {s.label}
                </Link>
              ))}
            </span>
          }
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader eyebrow="Departments" title="GTM" subtitle="Who to chase next and which channels create pipeline.">
        {visibleSections.length > 1 && (
          <TabBar tabs={visibleSections} active={active} onSelect={(id) => navigate(`/gtm/${id}`)} />
        )}
      </PageHeader>

      {active === 'accounts' && <AccountsView />}
      {active === 'funnel' && <FunnelView />}
      {active === 'points' && <PointsView />}
      {active === 'sourcing' && <SourcingView />}
      {active === 'plays' && <PlaysView />}
      {active === 'channels' && <ChannelsView />}
      {active === 'model' && <ModelView />}
      {active === 'matches' && <MatchReviewView />}
      {active === 'health' && <HealthView />}
    </PageShell>
  );
}
